import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

const RatingModal = ({ isOpen, onClose, orderId, reviewerRole = 'customer', driverName }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const starLabels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

  const handlePhotoSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 5 * 1024 * 1024) {
      setError('Photo must be under 5MB');
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader?.result);
    reader?.readAsDataURL(file);
    setError(null);
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let photoUrl = null;

      // Upload photo if provided
      if (photoFile) {
        const ext = photoFile?.name?.split('.')?.pop();
        const fileName = `ratings/${orderId}/${user?.id}_${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('order-photos')
          ?.upload(fileName, photoFile, { upsert: true });
        if (!uploadError && uploadData) {
          const { data: urlData } = supabase?.storage
            ?.from('order-photos')
            ?.getPublicUrl(fileName);
          photoUrl = urlData?.publicUrl || null;
        }
        // If upload fails, continue without photo
      }

      const { error: insertError } = await supabase
        ?.from('order_ratings')
        ?.insert({
          order_id: orderId,
          reviewer_id: user?.id,
          reviewer_role: reviewerRole,
          rating,
          review_text: reviewText?.trim() || null,
          photo_url: photoUrl,
        });

      if (insertError) throw insertError;

      setSubmitted(true);
      setTimeout(() => {
        onClose?.();
      }, 2000);
    } catch (err) {
      console.error('Rating submit error:', err);
      setError(err?.message || 'Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-[#0a1628] rounded-3xl shadow-2xl border border-[#ffd700]/20 overflow-hidden">
        {submitted ? (
          /* Success state */
          (<div className="p-8 text-center">
            <div className="relative mx-auto mb-5 w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-green-400/20 animate-ping" />
              <div className="relative w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" className="w-10 h-10">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
            </div>
            <h3 className="text-white font-black text-xl mb-2">Thank you!</h3>
            <p className="text-white/50 text-sm">Your rating has been submitted.</p>
          </div>)
        ) : (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#ffd700]/10 to-transparent px-6 pt-6 pb-4 border-b border-white/5">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-4 h-4">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-2xl bg-[#ffd700] flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="#0a1628" className="w-5 h-5">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-white font-black text-lg leading-tight">Rate Your Experience</h2>
                  <p className="text-white/40 text-xs">
                    {reviewerRole === 'customer'
                      ? `How was your delivery${driverName ? ` with ${driverName}` : ''}?`
                      : 'How was this delivery?'}
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Star Rating */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform active:scale-90 hover:scale-110"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="w-10 h-10 transition-colors duration-150"
                        fill={(hoverRating || rating) >= star ? '#ffd700' : 'none'}
                        stroke={(hoverRating || rating) >= star ? '#ffd700' : '#ffffff30'}
                        strokeWidth="1.5"
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    </button>
                  ))}
                </div>
                <p className="text-[#ffd700] text-sm font-bold h-5">
                  {starLabels?.[hoverRating || rating] || ''}
                </p>
              </div>

              {/* Photo Upload */}
              <div>
                <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                  Add Photo <span className="text-white/25 normal-case font-normal">(optional)</span>
                </p>
                {photoPreview ? (
                  <div className="relative rounded-2xl overflow-hidden border border-white/10">
                    <img
                      src={photoPreview}
                      alt="Selected photo preview for rating"
                      className="w-full h-32 object-cover"
                    />
                    <button
                      onClick={handleRemovePhoto}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-3.5 h-3.5">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef?.current?.click()}
                    className="w-full h-20 rounded-2xl border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1.5 hover:border-[#ffd700]/40 hover:bg-[#ffd700]/5 transition-all"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff50" strokeWidth="1.5" className="w-6 h-6">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                    </svg>
                    <span className="text-white/30 text-xs">Tap to upload photo</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
              </div>

              {/* Review Text */}
              <div>
                <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                  Review <span className="text-white/25 normal-case font-normal">(optional)</span>
                </p>
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e?.target?.value)}
                  placeholder="Share your experience..."
                  maxLength={300}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#ffd700]/40 focus:bg-white/8 resize-none transition-colors"
                />
                <p className="text-white/20 text-xs text-right mt-1">{reviewText?.length}/300</p>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                  <svg viewBox="0 0 24 24" fill="#ef4444" className="w-4 h-4 flex-shrink-0">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                  <p className="text-red-400 text-xs">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting || rating === 0}
                className="w-full py-4 rounded-2xl font-black text-sm transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: rating > 0 ? '#ffd700' : '#ffffff15',
                  color: rating > 0 ? '#0a1628' : '#ffffff40',
                }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit Rating'
                )}
              </button>

              <button
                onClick={onClose}
                className="w-full py-2 text-white/30 text-sm hover:text-white/50 transition-colors"
              >
                Skip for now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RatingModal;
